import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganization,
  useUpdateOrganization,
} from "@/api/hooks/organizations";

export default function OrganizationForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existing, isLoading } = useOrganization(id);
  const create = useCreateOrganization();
  const update = useUpdateOrganization(id ?? "");
  const remove = useDeleteOrganization();

  const [name, setName] = useState("");
  const [displayId, setDisplayId] = useState("");
  const [priorRelationshipNotes, setPriorRelationshipNotes] = useState("");
  const [warmIntroSource, setWarmIntroSource] = useState("");
  const [outreachStartedAt, setOutreachStartedAt] = useState("");
  const [masterMsaSignedAt, setMasterMsaSignedAt] = useState("");
  const [masterMsaReference, setMasterMsaReference] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDisplayId(existing.display_id ?? "");
      setPriorRelationshipNotes(existing.prior_relationship_notes ?? "");
      setWarmIntroSource(existing.warm_intro_source ?? "");
      setOutreachStartedAt(
        existing.outreach_started_at
          ? existing.outreach_started_at.slice(0, 10)
          : ""
      );
      setMasterMsaSignedAt(existing.master_msa_signed_at ?? "");
      setMasterMsaReference(existing.master_msa_reference ?? "");
      setNotes(existing.notes ?? "");
    }
  }, [existing]);

  if (isEdit && isLoading) return <CircularProgress />;

  const submit = async () => {
    setError(null);
    const payload = {
      name: name.trim(),
      display_id: displayId.trim() || null,
      prior_relationship_notes: priorRelationshipNotes.trim() || null,
      warm_intro_source: warmIntroSource.trim() || null,
      outreach_started_at: outreachStartedAt
        ? `${outreachStartedAt}T00:00:00`
        : null,
      master_msa_signed_at: masterMsaSignedAt || null,
      master_msa_reference: masterMsaReference.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      if (isEdit) await update.mutateAsync(payload);
      else await create.mutateAsync(payload);
      navigate("/clinops/sites/organizations");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEdit ? "Edit organization" : "New organization"}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack spacing={2}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextField
          label="Display ID"
          value={displayId}
          onChange={(e) => setDisplayId(e.target.value)}
          helperText="Optional smart code (e.g., O-100). Unique across all organizations."
        />
        <TextField
          label="Master MSA reference"
          value={masterMsaReference}
          onChange={(e) => setMasterMsaReference(e.target.value)}
        />
        <TextField
          label="Master MSA signed"
          type="date"
          value={masterMsaSignedAt}
          onChange={(e) => setMasterMsaSignedAt(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Warm intro source"
          value={warmIntroSource}
          onChange={(e) => setWarmIntroSource(e.target.value)}
          helperText="Inherited by attached Sites when their own value is empty."
        />
        <TextField
          label="Outreach started"
          type="date"
          value={outreachStartedAt}
          onChange={(e) => setOutreachStartedAt(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Prior relationship notes"
          value={priorRelationshipNotes}
          onChange={(e) => setPriorRelationshipNotes(e.target.value)}
          multiline
          rows={3}
          helperText="Inherited by attached Sites when their own value is empty."
        />
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Box>
            {isEdit && (
              <Button
                color="error"
                onClick={async () => {
                  if (!id) return;
                  if (!confirm("Delete this organization?")) return;
                  try {
                    await remove.mutateAsync(id);
                    navigate("/clinops/sites/organizations");
                  } catch (e) {
                    setError(
                      e instanceof Error ? e.message : "Failed to delete"
                    );
                  }
                }}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => navigate("/clinops/sites/organizations")}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={
                !name.trim() || create.isPending || update.isPending
              }
            >
              {isEdit ? "Save" : "Create"}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
